import React from 'react';
import { Grid, TextField, Typography, IconButton, Box, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DatosPaquete } from './EnvioNacionalModal.types';
import { formatMilesCO, parseMilesCO } from './EnvioNacionalModal.utils';

const compactFieldSx = {
  '& .MuiFormHelperText-root': { mt: 0.75, mx: 0, minHeight: '1.1em', lineHeight: 1.2 },
};

const NUMERIC_RE = /\D/g;
const ALPHANUMERIC_RE = /[^a-zA-Z0-9\s.,#_áéíóúÁÉÍÓÚñÑüÜ-]/g;

interface Props {
  index: number;
  datos: DatosPaquete;
  onChange: (index: number, field: keyof DatosPaquete, value: string | number) => void;
  touched: Partial<Record<keyof DatosPaquete, boolean>>;
  onBlur: (index: number, field: keyof DatosPaquete) => void;
  onEliminar?: (index: number) => void;
  canEliminar?: boolean;
  esColombia?: boolean;
  modoCotizador?: boolean;
}

const getDimensionError = (val: string): string => {
  if (!val) return 'Campo requerido';
  if (Number(val) < 1) return 'El valor no puede ser menor a 1';
  if (val.length > 3) return 'Máx. 3 dígitos';
  return '';
};

const getPesoError = (val: string): string => {
  if (!val) return 'Campo requerido';
  if (val.length > 4) return 'Máx. 4 dígitos';
  return '';
};

const getContenidoError = (val: string): string => {
  if (!val) return 'Campo requerido';
  return '';
};

const getValorDeclaradoPaqueteError = (val: string): string => {
  if (!val) return 'Campo requerido';
  if (val.length > 16) return 'Máx. 16 dígitos';
  return '';
};

export const getPaqueteFieldError = (
  field: keyof DatosPaquete,
  datos: DatosPaquete,
  esColombia = false,
): string => {
  switch (field) {
    case 'largo':    return getDimensionError(datos.largo);
    case 'alto':     return getDimensionError(datos.alto);
    case 'ancho':    return getDimensionError(datos.ancho);
    case 'peso':     return getPesoError(datos.peso);
    case 'contenido': return getContenidoError(datos.contenido);
    case 'valorDeclarado':
      return esColombia ? getValorDeclaradoPaqueteError(datos.valorDeclarado ?? '') : '';
    default: return '';
  }
};

export const isPaqueteValido = (datos: DatosPaquete, esColombia = false, modoCotizador = false): boolean => {
  const baseFields: (keyof DatosPaquete)[] = modoCotizador
    ? ['largo', 'alto', 'ancho', 'peso']
    : ['largo', 'alto', 'ancho', 'peso', 'contenido'];
  const ok = baseFields.every(f => getPaqueteFieldError(f, datos, esColombia) === '');
  if (!ok) return false;
  if (esColombia) return getValorDeclaradoPaqueteError(datos.valorDeclarado ?? '') === '';
  return true;
};

const calcPesoVolumen = (alto: string, largo: string, ancho: string): number => {
  const a = Number(alto) || 0;
  const l = Number(largo) || 0;
  const an = Number(ancho) || 0;
  return (a * l * an) / 2500;
};

export const PaqueteItem: React.FC<Props> = ({
  index, datos, onChange, touched, onBlur, onEliminar,
  canEliminar = false, esColombia = false, modoCotizador = false,
}) => {
  const fieldError = (field: keyof DatosPaquete): string =>
    touched[field] ? getPaqueteFieldError(field, datos, esColombia) : '';

  const handleNumericChange = (field: 'largo' | 'alto' | 'ancho', maxLen: number) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(index, field, e.target.value.replaceAll(NUMERIC_RE, '').slice(0, maxLen));
    };

  const handlePeso = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, 'peso', e.target.value.replaceAll(NUMERIC_RE, '').slice(0, 4));
  };

  const handleCantidad = (delta: number) => {
    const next = Math.max(1, datos.cantidad + delta);
    onChange(index, 'cantidad', next);
  };

  const handleCantidadInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number.parseInt(e.target.value.replaceAll(NUMERIC_RE, '') || '1', 10);
    onChange(index, 'cantidad', Math.max(1, val));
  };

  const handleContenido = (e: React.ChangeEvent<HTMLInputElement>) => {
    const limpio = e.target.value.replaceAll(ALPHANUMERIC_RE, '').slice(0, 160);
    onChange(index, 'contenido', limpio);
  };

  const handleValorDeclarado = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, 'valorDeclarado', parseMilesCO(e.target.value).slice(0, 16));
  };

  const pesoVolumen = esColombia
    ? calcPesoVolumen(datos.alto, datos.largo, datos.ancho)
    : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>
          Paquete {index + 1}
        </Typography>
        {canEliminar && (
          <Tooltip title="Eliminar paquete">
            <IconButton
              size="small" color="error"
              onClick={() => onEliminar?.(index)}
              aria-label={`eliminar paquete ${index + 1}`}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={1}>
        {/* Largo / Alto / Ancho */}
        <Grid size={{ xs: 4 }}>
          <TextField
            fullWidth required size="small" sx={compactFieldSx} label="Largo (cm)"
            value={datos.largo}
            onChange={handleNumericChange('largo', 3)}
            onBlur={() => onBlur(index, 'largo')}
            error={!!fieldError('largo')}
            helperText={fieldError('largo') || ' '}
            inputMode="numeric"
            slotProps={{ htmlInput: { maxLength: 3 } }}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TextField
            fullWidth required size="small" sx={compactFieldSx} label="Alto (cm)"
            value={datos.alto}
            onChange={handleNumericChange('alto', 3)}
            onBlur={() => onBlur(index, 'alto')}
            error={!!fieldError('alto')}
            helperText={fieldError('alto') || ' '}
            inputMode="numeric"
            slotProps={{ htmlInput: { maxLength: 3 } }}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TextField
            fullWidth required size="small" sx={compactFieldSx} label="Ancho (cm)"
            value={datos.ancho}
            onChange={handleNumericChange('ancho', 3)}
            onBlur={() => onBlur(index, 'ancho')}
            error={!!fieldError('ancho')}
            helperText={fieldError('ancho') || ' '}
            inputMode="numeric"
            slotProps={{ htmlInput: { maxLength: 3 } }}
          />
        </Grid>

        {/* Peso volumen: solo wizard Colombia, entero */}
        {esColombia && !modoCotizador && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Peso volumen:</strong> {Math.round(pesoVolumen ?? 0)} kg
            </Typography>
          </Grid>
        )}

        {/* Peso | Cantidad | Valor declarado (Colombia) */}
        <Grid size={{ xs: esColombia ? 4 : 6 }}>
          <TextField
            fullWidth required size="small" sx={compactFieldSx} label="Peso (kg)"
            value={datos.peso}
            onChange={handlePeso}
            onBlur={() => onBlur(index, 'peso')}
            error={!!fieldError('peso')}
            helperText={fieldError('peso') || ' '}
            inputMode="numeric"
            slotProps={{ htmlInput: { maxLength: 4 } }}
          />
        </Grid>
        <Grid size={{ xs: esColombia ? 4 : 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
              Cantidad
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center',
              border: '1px solid', borderColor: 'divider',
              borderRadius: 2, overflow: 'hidden',
            }}>
              <IconButton
                size="small" onClick={() => handleCantidad(-1)}
                disabled={datos.cantidad <= 1} aria-label="decrementar cantidad"
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <TextField
                value={datos.cantidad}
                onChange={handleCantidadInput}
                inputMode="numeric"
                variant="standard"
                slotProps={{
                  input: { disableUnderline: true },
                  htmlInput: { style: { textAlign: 'center', width: 32 }, maxLength: 4 },
                }}
              />
              <IconButton size="small" onClick={() => handleCantidad(1)} aria-label="incrementar cantidad">
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Grid>

        {/* Valor declarado por paquete (Colombia) */}
        {esColombia && (
          <Grid size={{ xs: 4 }}>
            <TextField
              fullWidth required size="small" sx={compactFieldSx} label="Valor declarado"
              value={esColombia ? formatMilesCO(datos.valorDeclarado ?? '') : (datos.valorDeclarado ?? '')}
              onChange={handleValorDeclarado}
              onBlur={() => onBlur(index, 'valorDeclarado')}
              error={!!fieldError('valorDeclarado')}
              helperText={fieldError('valorDeclarado') || ' '}
              inputMode="numeric"
              slotProps={{ htmlInput: { maxLength: 16 } }}
            />
          </Grid>
        )}

        {/* Contenido — oculto en cotizador */}
        {!modoCotizador && (
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth required size="small" sx={compactFieldSx} label="Contenido del paquete"
              value={datos.contenido}
              onChange={handleContenido}
              onBlur={() => onBlur(index, 'contenido')}
              error={!!fieldError('contenido')}
              helperText={fieldError('contenido') || `${datos.contenido.length}/160`}
              slotProps={{ htmlInput: { maxLength: 160 } }}
            />
          </Grid>
        )}

        {/* Campos Colombia opcionales — no aplican en cotizador */}
        {esColombia && !modoCotizador && (
          <>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth size="small" sx={compactFieldSx} label="Observaciones de la guía"
                value={datos.observacionesGuia ?? ''}
                onChange={e =>
                  onChange(index, 'observacionesGuia',
                    e.target.value.replaceAll(ALPHANUMERIC_RE, '').slice(0, 160))
                }
                helperText={`${(datos.observacionesGuia ?? '').length}/160`}
                slotProps={{ htmlInput: { maxLength: 160 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth size="small" sx={compactFieldSx} label="Referencia"
                value={datos.documentoReferencia ?? ''}
                onChange={e =>
                  onChange(index, 'documentoReferencia',
                    e.target.value.replaceAll(ALPHANUMERIC_RE, '').slice(0, 160))
                }
                helperText={`${(datos.documentoReferencia ?? '').length}/160`}
                slotProps={{ htmlInput: { maxLength: 160 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth size="small" sx={compactFieldSx} label="Código evento"
                value={datos.codigoEvento ?? ''}
                onChange={e =>
                  onChange(index, 'codigoEvento',
                    e.target.value.replaceAll(NUMERIC_RE, '').slice(0, 5))
                }
                inputMode="numeric"
                helperText={`${(datos.codigoEvento ?? '').length}/5`}
                slotProps={{ htmlInput: { maxLength: 5 } }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};
